import React, { useCallback, useEffect, useState } from 'react';
import FeatherIcon from '../FeatherIcon';
import { OFFERED, FOUNDERS_THEME, estimatedContributionCents, formatPrice } from '../../products/editions';
import { ProductImageFrame, StatusChip, scrollToSection, usePrefersReducedMotion } from './shared';

const AUTOPLAY_MS = 7000;

/**
 * The four-edition carousel (WAI-ARIA APG carousel pattern). Autoplay rotates only until someone
 * uses a control, never runs under prefers-reduced-motion, and pauses while the pointer or focus
 * is inside it. Slides share one grid cell, so the tallest one sizes the band and switching never
 * shifts the page; a slide's image is mounted only once it is current or next.
 */
const ProductHero: React.FC = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [mounted, setMounted] = useState<ReadonlySet<number>>(() => new Set([0, 1]));
  const count = OFFERED.length;
  const rotating = playing && !reducedMotion && !hovered && !focused;

  const show = useCallback(
    (next: number) => {
      const target = (next + count) % count;
      setIndex(target);
      setMounted((seen) => new Set(seen).add(target).add((target + 1) % count));
    },
    [count]
  );

  const go = (next: number): void => {
    setPlaying(false);
    show(next);
  };

  useEffect(() => {
    if (!rotating) return undefined;
    const timer = window.setTimeout(() => show(index + 1), AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [rotating, index, show]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowRight') go(index + 1);
    else if (e.key === 'ArrowLeft') go(index - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(count - 1);
    else return;
    e.preventDefault();
  };

  const autoplayAvailable = !reducedMotion;
  const current = OFFERED[index];
  const controlTone = current.theme.dark
    ? 'bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white'
    : 'bg-card/80 text-gray-800 hover:bg-card focus-visible:ring-primary';

  return (
    <section
      aria-roledescription="carousel"
      aria-label="The Pawsitive Deck editions"
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      onKeyDown={onKeyDown}
    >
      <div className="grid" aria-live={rotating ? 'off' : 'polite'}>
        {OFFERED.map((edition, i) => {
          const active = i === index;
          const { theme } = edition;
          return (
            <div
              key={edition.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}: ${edition.name}`}
              aria-hidden={!active}
              className={`[grid-area:1/1] ${theme.surface} transition-[opacity,visibility] duration-700 motion-reduce:transition-none ${
                active ? 'visible opacity-100' : 'invisible opacity-0'
              }`}
            >
              <div className="container max-w-7xl mx-auto px-6 pt-8 pb-24 lg:pt-14 lg:pb-24 grid gap-8 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-7 lg:order-2">
                  {mounted.has(i) ? (
                    <ProductImageFrame
                      image={edition.hero.image}
                      label={`${edition.name} concept`}
                      theme={theme}
                      gold={theme === FOUNDERS_THEME}
                      aspect="aspect-[4/3] lg:aspect-[16/11]"
                      eager={i === 0}
                      className={theme.dark ? 'bg-white/5' : 'bg-card/60'}
                    />
                  ) : (
                    <div className="aspect-[4/3] lg:aspect-[16/11]" />
                  )}
                </div>
                <div className="lg:col-span-5 lg:order-1">
                  <StatusChip className={`${theme.accent} ${theme.dark ? 'border-white/25' : 'border-current'}`}>
                    Proposed edition
                  </StatusChip>
                  <p className={`mt-4 text-lg font-semibold ${theme.accent}`}>
                    {edition.name} <span aria-hidden="true">|</span>
                    <span className="sr-only">,</span> {formatPrice(edition.priceCents)}
                  </p>
                  <h2 className={`mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight ${theme.ink}`}>
                    {edition.hero.headline}
                  </h2>
                  <p className={`mt-4 text-lg ${theme.muted}`}>{edition.hero.subheading}</p>
                  <p className={`mt-4 text-sm ${theme.muted}`}>
                    Estimated voucher contribution:{' '}
                    <span className={`font-semibold ${theme.ink}`}>
                      {formatPrice(estimatedContributionCents(edition))}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => scrollToSection(`edition-${edition.id}`)}
                    className={`mt-6 px-8 py-3 ${theme.button}`}
                  >
                    {edition.hero.cta}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-3 px-6">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="Previous edition"
          className={`flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 ${controlTone}`}
        >
          <FeatherIcon name="chevron-left" size={20} />
        </button>
        <div className="flex items-center gap-2">
          {OFFERED.map((edition, i) => (
            <button
              key={edition.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`Show slide ${i + 1}: ${edition.name}`}
              aria-current={i === index ? 'true' : undefined}
              className="flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
            >
              <span
                className={`block h-2.5 rounded-full transition-all motion-reduce:transition-none ${
                  i === index ? 'w-6' : 'w-2.5 opacity-50'
                } ${current.theme.dark ? 'bg-white' : 'bg-gray-700'}`}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="Next edition"
          className={`flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 ${controlTone}`}
        >
          <FeatherIcon name="chevron-right" size={20} />
        </button>
        {autoplayAvailable && (
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? 'Pause automatic slide show' : 'Start automatic slide show'}
            className={`ml-2 flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 ${controlTone}`}
          >
            <FeatherIcon name={playing ? 'pause' : 'play'} size={18} />
          </button>
        )}
      </div>
    </section>
  );
};

export default ProductHero;
